import React, { useMemo } from "react";
import { Window, WindowActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { Input, Switch, TextArea } from "@progress/kendo-react-inputs";
import { Label } from "@progress/kendo-react-labels";

import MessageSchema from "src/utils/messageSchema";
import { LoadingPanel } from "src/components/loadingPanel";
import { setNewMessage } from "src/redux/actions/setting-actions";
import { useDispatch } from "react-redux";
import { getUserCredential } from "src/lib/cookies";

import RenderGroup from "../filterQuery/renderGroupReports";
import ReportSavedQueryGrid from "./savedQueryGrid";
import {
  createEmptyGroup,
  collectFilters,
  getOperatorListValue,
  getDefaultExpressionData,
  normalizeFilterGroups,
  renderTooltip,
  handleRenderValidation,
  flatId,
  isSelectAllId,
  isSelectAllSelection,
} from "../filterQuery/filterUtils";
import useAPI from "../filterQuery/service.js";
import useReportFilterService from "./reportFilterService";
import { useAdvanceQueryFilterData } from "./useAdvanceQueryFilterData";

const resetFormFields = () => ({
  filter_name: "",
  filter_desc: "",
  applyToGrid: false,
  isFavorite: false,
  version: 0,
});

const resetErrors = () => ({
  filter_name: "",
});

const getItemField = (item) => item?.field || item?.value?.field || item?.inputValue?.field || "";

const orderCascadeFields = (fieldsSet, preferredOrder = []) => {
  const ordered = [];
  const seen = new Set();
  for (const field of preferredOrder) {
    if (fieldsSet.has(field) && !seen.has(field)) {
      ordered.push(field);
      seen.add(field);
    }
  }
  for (const field of fieldsSet) {
    if (!seen.has(field)) ordered.push(field);
  }
  return ordered;
};

const optionsFromActionResult = (result) => {
  const mapped = Array.isArray(result?.mappedData) ? result.mappedData : [];
  if (!mapped.length) return [];
  if (mapped.some((opt) => opt.id === "Select All" || opt.id === "0")) return mapped;
  return [{ id: "Select All", text: "Select All", dataItemKey: mapped[0]?.dataItemKey }, ...mapped];
};

/**
 * Reusable query-filter window. Columns/fieldConfigMap/actions can be passed
 * as props (overrides) or resolved automatically by useAdvanceQueryFilterData
 * based on config.module.
 */
const AdvanceQueryFilter = ({
  title = "Advance Search",
  open = true,
  onClose,
  onApply,
  onSavedFilterUpdate,
  columns: columnsProp,
  fieldConfigMap: fieldConfigMapProp,
  actions: actionsProp,
  config,
  renderTopFilters,
  savedAppliedFilter,
  onDirectApplyLoading,
  initialToggleValues,
  resetSignal = 0,
}) => {
  const dispatch = useDispatch();

  const dbObjName = config?.dbObjName || "";
  const module = config?.module || "report";
  const showDefaultExpression = Boolean(config?.showDefaultExpression);
  const defaultExpressionField = config?.defaultExpressionField || null;

  const hookData = useAdvanceQueryFilterData(config);

  const columns = columnsProp ?? hookData.columns;
  // config.requiredFields takes precedence; falls back to API-derived required fields (from is_nullable).
  const requiredFields = config?.requiredFields ?? hookData.requiredFields ?? null;
  const fieldConfigMap = fieldConfigMapProp ?? hookData.fieldConfigMap;
  const actions = actionsProp ?? hookData.actions;
  const toggleFieldDefs = hookData.toggleFieldDefs ?? [];
  const cascadeFields = React.useMemo(
    () => hookData.cascadeFields ?? [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config?.module]
  );
  const { operatorList, fetchOperators } = hookData;

  // Masks fields that are loading context-specific data — replaces .data with [] to prevent stale drops.
  const [advanceLoadingFields, setAdvanceLoadingFields] = React.useState(() => new Set());

  const effectiveFieldConfigMap = React.useMemo(() => {
    if (!advanceLoadingFields.size) return fieldConfigMap;
    const result = {};
    for (const [key, cfg] of Object.entries(fieldConfigMap)) {
      result[key] = advanceLoadingFields.has(key) ? { ...cfg, data: [], originalData: [] } : cfg;
    }
    return result;
  }, [fieldConfigMap, advanceLoadingFields]);

  const [filterColumns, setFilterColumnsData] = React.useState([]);
  const [originalFilterColumns, setOriginalFilterColumns] = React.useState([]);

  const [addData, setData] = React.useState(createEmptyGroup());
  const [formFields, setFormFields] = React.useState(resetFormFields);
  const [errObj, setErrObj] = React.useState(resetErrors);

  const [gridHistoryId, setGridHistoryId] = React.useState("");
  const [appliedFilterQuery, setAppliedFilterQuery] = React.useState("");

  const [loadingState, setLoadingState] = React.useState(false);
  const [isFieldChange, setIsFieldChange] = React.useState(false);
  const [groupCounter, setGroupCounter] = React.useState(0);
  const [expressionCounter, setExpressionCounter] = React.useState(0);

  const expressionCounterRef = React.useRef(expressionCounter);
  expressionCounterRef.current = expressionCounter;
  // Incremented on every close — lets in-flight getDataById calls detect they are stale.
  const fetchGenerationRef = React.useRef(0);
  const handleClose = React.useCallback(() => {
    fetchGenerationRef.current += 1;
    onClose?.({ hasExpressions: expressionCounterRef.current > 0 });
  }, [onClose]);

  const [mainLogic, setMainLogic] = React.useState("and");
  const [toggleValues, setToggleValues] = React.useState({});
  const initialToggleValuesRef = React.useRef(initialToggleValues);
  initialToggleValuesRef.current = initialToggleValues;
  // True while RenderGroup has any cascade fetch in-flight.
  const [isCascadeActive, setIsCascadeActive] = React.useState(false);
  const reportFilterService = useReportFilterService();

  const errorMessage = useMemo(() => {
    return `You can add maximum of ${filterColumns?.length || 0} expressions`;
  }, [filterColumns?.length]);

  // eslint-disable-next-line no-unused-vars
  const [list, Action] = useAPI();
  // Stable ref — useAPI returns a new Action object on every render.
  const ActionRef = React.useRef(Action);
  React.useEffect(() => {
    ActionRef.current = Action;
  });

  // Determine if this is a report module
  const isReportModule =
    module && (module.includes("_report") || module === "plan_comp_statement" || module === "provider_comp_report");

  // Conditional API wrapper — routes to report or event service based on module type
  const apiService = React.useMemo(() => {
    if (isReportModule) {
      return {
        GetFilterQUery: async (reqData) => {
          const res = await reportFilterService.loadReportFilters(reqData);
          return res?.data?.row_result || [];
        },
        SaveFilterQuery: async (reqData) => {
          const res = await reportFilterService.saveReportFilter(reqData);
          if (res?.data?.out_parameters?.out_ret_code === 0) {
            return {
              out_last_insert_key: res.data.out_parameters.out_last_insert_key,
              success: true,
            };
          }
          return {
            out_last_insert_key: "",
            success: false,
            errorMsg: res?.data?.out_parameters?.out_error_msg || "",
          };
        },
        UpdateFilterQuery: async (reqData) => {
          const res = await reportFilterService.updateReportFilter(reqData);
          if (res?.data?.out_parameters?.out_ret_code === 0) {
            return { success: true };
          }
          return {
            success: false,
            errorMsg: res?.data?.out_parameters?.out_error_msg || "",
          };
        },
        DeleteFilterQuery: async (reqData) => {
          const res = await reportFilterService.deleteReportFilter(reqData);
          return res?.data || {};
        },
      };
    }
    return ActionRef.current;
  }, [isReportModule, reportFilterService]);

  const [tooltipString, setTooltipString] = React.useState("");

  const [showSavedList, setShowSavedList] = React.useState(false);
  const [isCreateNewQueryClick, setCreateNewQueryClick] = React.useState(false);
  // eslint-disable-next-line no-unused-vars
  const [isDataLoading, setDataLoading] = React.useState(true);
  // Per-field loading set — drives spinners while a saved-query load is in flight.
  const [fetchLoadingFields, setFetchLoadingFields] = React.useState(new Set());
  // True only while a dropdown fetch is in flight. An empty result ("No data found")
  // is a finished load — Search/Save must stay enabled.
  const hasExpressionDropdownsLoading = React.useMemo(() => {
    const walk = (items) => {
      if (!items?.length) return false;
      for (const item of items) {
        if (item.items && walk(item.items)) return true;
        if (!item.value?.dropDownControl) continue;
        const field = item.value?.field;
        if (!field) continue;
        if (advanceLoadingFields.has(field) || fetchLoadingFields.has(field)) return true;
        const cfg = effectiveFieldConfigMap?.[field];
        if (cfg?.loading) return true;
      }
      return false;
    };
    return walk(addData?.items);
  }, [addData, effectiveFieldConfigMap, advanceLoadingFields, fetchLoadingFields]);
  // Disable Search/Save/nav until every expression dropdown has finished loading.
  const isActionDisabled =
    isCascadeActive || fetchLoadingFields.size > 0 || advanceLoadingFields.size > 0 || hasExpressionDropdownsLoading;
  const actionDisabledTitle = isActionDisabled ? "Please wait for field data to finish loading" : undefined;
  // eslint-disable-next-line no-unused-vars
  const [mode, setMode] = React.useState("");

  // Baseline for “Create new query” — default initial expression is not a user edit.
  const baselineRef = React.useRef({
    formFieldsJson: JSON.stringify(resetFormFields()),
    gridHistoryId: "",
  });
  const hasFetchedOperatorsRef = React.useRef(false);
  const fetchOperatorsRef = React.useRef(fetchOperators);
  React.useEffect(() => {
    fetchOperatorsRef.current = fetchOperators;
  }, [fetchOperators]);
  const columnsRef = React.useRef(null);
  // Stable ref to latest savedAppliedFilter — avoids adding the prop to effect deps.
  const savedAppliedFilterRef = React.useRef(savedAppliedFilter);
  savedAppliedFilterRef.current = savedAppliedFilter;

  // Stable ref to latest fieldConfigMap — avoids stale closure in getDataById.
  const fieldConfigMapRef = React.useRef(fieldConfigMap);
  fieldConfigMapRef.current = fieldConfigMap;
  React.useEffect(() => {
    fieldConfigMapRef.current = fieldConfigMap;
  });

  const cascadePreferredOrder = React.useMemo(
    () => ["comp_plan_key", "comp_plan_mstr_key", "period_key", "period_mstr_key", ...(cascadeFields || [])],
    [cascadeFields]
  );

  const clearDropdownData = React.useCallback(() => {
    const clearFieldData = hookData?.clearFieldData;
    if (clearFieldData) {
      Object.values(clearFieldData).forEach((fn) => {
        if (typeof fn === "function") fn();
      });
    }
    Object.values(fieldConfigMapRef.current || {}).forEach((cfg) => {
      if (cfg?._calledOnce) cfg._calledOnce = {};
    });
  }, [hookData?.clearFieldData]);

  const showErrorMsg = React.useCallback(
    (msg) => {
      return dispatch(
        setNewMessage({
          message: msg,
          type: "Error",
          show: true,
        })
      );
    },
    [dispatch]
  );

  // Effect 1 — Restore or reset state exactly once per open transition.
  React.useEffect(() => {
    if (!open) return;

    setAdvanceLoadingFields(new Set());
    setFetchLoadingFields(new Set());
    setIsCascadeActive(false);
    calledOnceRef.current = {};
    clearDropdownData();

    const saved = savedAppliedFilterRef.current;
    if (saved?.filterData) {
      const generation = fetchGenerationRef.current;
      const restoredForm = saved.formFields || resetFormFields();
      setFormFields(restoredForm);

      // Collect the dropdown fields (and their saved values) from the restored
      // filter. 
      const fieldsInFilter = new Set();
      const savedValues = {};
      const walkItems = (data) => {
        if (!data?.items) return;
        data.items.forEach((it) => {
          if (it.items) return walkItems(it);
          const field = getItemField(it);
          if (!field) return;
          fieldsInFilter.add(field);
          if (it.inputValue?.value != null && it.inputValue.value !== "") {
            savedValues[field] = it.inputValue.value;
          }
        });
      };
      walkItems(saved.filterData);
      for (const field of fieldsInFilter) {
        calledOnceRef.current[field] = true;
        const cfg = fieldConfigMapRef.current?.[field];
        if (cfg) {
          if (!cfg._calledOnce) cfg._calledOnce = {};
          cfg._calledOnce[field] = true;
        }
      }

      setData(saved.filterData);
      setMainLogic(saved.mainLogic || "and");
      setGridHistoryId(saved.gridHistoryId || "");
      setAppliedFilterQuery(saved.appliedFilterQuery || "");
      setErrObj(resetErrors());
      const restoredCount = normalizeFilterGroups(collectFilters(saved.filterData, { isAdvanceFilter: true })).length;
      setExpressionCounter(restoredCount);
      setGroupCounter(0);
      setIsFieldChange((saved.filterData?.items?.length ?? 0) > 0);
      setToggleValues(saved.toggleValues || initialToggleValuesRef.current || {});
      baselineRef.current = {
        formFieldsJson: JSON.stringify(restoredForm),
        gridHistoryId: saved.gridHistoryId || "",
      };

      // Reload each dropdown's options with their
      // parent keys — same as getDataById's DB-restore path.
      (async () => {
        if (!fieldsInFilter.size) return;
        const orderedFields = orderCascadeFields(fieldsInFilter, cascadePreferredOrder);
        setFetchLoadingFields(new Set(orderedFields));
        setAdvanceLoadingFields(new Set(orderedFields));
        const cascadeContext = {};
        const isWindowClosed = () => fetchGenerationRef.current !== generation;
        for (const field of orderedFields) {
          if (fetchGenerationRef.current !== generation) {
            setFetchLoadingFields(new Set());
            setAdvanceLoadingFields(new Set());
            return;
          }
          const action = actions[field];
          if (typeof action === "function") {
            try {
              await action(cascadeContext, { isInitialLoad: false, isWindowClosed });
              const savedVal = savedValues[field];
              if (savedVal != null && savedVal !== "") cascadeContext[field] = savedVal;
            } catch (err) {
              console.error(`Failed to load data for field ${field}:`, err);
            } finally {
              setFetchLoadingFields((prev) => {
                const next = new Set(prev);
                next.delete(field);
                return next;
              });
            }
          }
        }
        setAdvanceLoadingFields((prev) => {
          const next = new Set(prev);
          for (const f of fieldsInFilter) next.delete(f);
          return next;
        });
      })();
    } else {
      const baseForm = resetFormFields();
      setFormFields(baseForm);
      setErrObj(resetErrors());
      setGridHistoryId("");
      setAppliedFilterQuery("");
      setIsFieldChange(false);
      setMainLogic("and");
      setExpressionCounter(0);
      setGroupCounter(0);
      setToggleValues({});
      setData(createEmptyGroup());
      baselineRef.current = {
        formFieldsJson: JSON.stringify(baseForm),
        gridHistoryId: "",
      };
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect 2 — Sync columns and fetch operators when they become available.
  React.useEffect(() => {
    if (!open) return;

    setLoadingState(true);
    try {
      if (columnsRef.current !== columns) {
        columnsRef.current = columns;
        setFilterColumnsData(columns);
        setOriginalFilterColumns(columns);
      }
      if (!Object.keys(operatorList || {}).length && !hasFetchedOperatorsRef.current) {
        hasFetchedOperatorsRef.current = true;
        fetchOperatorsRef.current?.();
      }
    } finally {
      setLoadingState(false);
    }
  }, [columns, open, operatorList]);

  React.useEffect(() => {
    if (!open) return;
    if (Object.keys(operatorList || {}).length) {
      hasFetchedOperatorsRef.current = false;
    }
  }, [open, operatorList]);

  React.useEffect(() => {
    if (!open) return;
    if (!showDefaultExpression) return;
    // Don't overwrite a restored filter with the default expression.
    if (savedAppliedFilterRef.current?.filterData) return;
    if (!columns.length || !Object.keys(operatorList || {}).length) return;

    const fieldToUse = defaultExpressionField
      ? columns.find((c) => c.field === defaultExpressionField) || columns[0]
      : columns[0];
    const defaultGroup = getDefaultExpressionData(
      columns,
      operatorList,
      fieldToUse.field,
      getOperatorListValue(fieldToUse.type, operatorList),
      fieldConfigMapRef.current
    );
    setData(defaultGroup);
    setExpressionCounter(defaultGroup?.items?.length || 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, open, operatorList, showDefaultExpression, defaultExpressionField]);

  const checkNumberOfExpressions = React.useCallback(() => {
    const filterObj = collectFilters(addData, { isAdvanceFilter: true });
    const resCount = normalizeFilterGroups(filterObj).length;
    return {
      isValid: resCount <= (filterColumns?.length || 0),
      count: resCount,
    };
  }, [addData, filterColumns?.length]);

  const toolTipMsg = React.useCallback(
    (filterObj) => {
      const latestFieldConfigMap = fieldConfigMapRef.current;
      const normalizedFieldConfigMap = {};
      for (const [field, cfg] of Object.entries(latestFieldConfigMap)) {
        if (Array.isArray(cfg.data) && cfg.data.some((d) => d.id === "Select All")) {
          normalizedFieldConfigMap[field] = {
            ...cfg,
            data: cfg.data.map((d) => (d.id === "Select All" ? { ...d, id: "0" } : d)),
          };
        } else {
          normalizedFieldConfigMap[field] = cfg;
        }
      }
      const tooltip = renderTooltip(filterObj, originalFilterColumns, normalizedFieldConfigMap);
      setTooltipString(tooltip);
      return tooltip;
    },
    [originalFilterColumns]
  );

  const getDataById = React.useCallback(
    async (id, { skipDropdownLoad = false } = {}) => {
      if (!filterColumns.length || !Object.keys(operatorList).length) return;
      // Snapshot the generation so we can detect if the window was closed mid-fetch.
      const generation = fetchGenerationRef.current;
      const cookiesData = getUserCredential();
      const reqData = {
        in_tenant_id: cookiesData?.out_tenant_id || "",
        in_filter: `AND (a.grid_filter_history_key = "${id}")`,
        in_sort_number: 9,
        in_sort_order: "DESC",
        in_page_row_offset: 0,
        in_page_rows: 9999,
        in_user_id: cookiesData?.out_user_id || "",
        in_db_obj_code: dbObjName,
        filterColumns,
        operatorList,
        module,
      };
      const res = await apiService.GetFilterQUery(reqData);
      const record = res?.[0];
      if (!record) return;
      // Window was closed while the API call was in-flight — abandon silently.
      if (fetchGenerationRef.current !== generation) return;
      setGridHistoryId(record.grid_filter_history_key);
      setAppliedFilterQuery(record.filter_text || "");
      setFormFields((prev) => ({
        ...prev,
        filter_name: record.filter_name || "",
        filter_desc: record.filter_desc || "",
        isFavorite: Boolean(Number(record.is_favorite)),
        version: record.version || 0,
      }));

      // Restore toggle values saved with this filter (e.g. Provider Detail switch)
      if (record.toggleValues && typeof record.toggleValues === "object") {
        setToggleValues(record.toggleValues);
      }

      const fieldsInFilter = new Set();
      const collectFields = (data) => {
        if (!data || !data.items) return;
        data.items.forEach((item) => {
          if (item.items) {
            collectFields(item);
          } else {
            const field = getItemField(item);
            if (field) fieldsInFilter.add(field);
          }
        });
      };
      collectFields(record.filterData);

      // Extract saved values to use as cascade context when reloading dropdowns.
      const savedValues = {};
      const extractSavedValues = (data) => {
        if (!data || !data.items) return;
        data.items.forEach((item) => {
          if (item.items) {
            extractSavedValues(item);
          } else {
            const field = getItemField(item);
            if (field && item.inputValue?.value != null && item.inputValue.value !== "") {
              savedValues[field] = item.inputValue.value;
            }
          }
        });
      };
      extractSavedValues(record.filterData);

      const orderedFields = orderCascadeFields(fieldsInFilter, cascadePreferredOrder);

      // Stamp calledOnce guards before setData so lazy-load effects don't fire context-free fetches.
      for (const field of orderedFields) {
        calledOnceRef.current[field] = true;
        const cfg = fieldConfigMapRef.current?.[field];
        if (cfg) {
          if (!cfg._calledOnce) cfg._calledOnce = {};
          cfg._calledOnce[field] = true;
        }
      }

      if (!skipDropdownLoad) {
        clearDropdownData();
        setFetchLoadingFields(new Set(orderedFields));
        setAdvanceLoadingFields(new Set(orderedFields));
      }

      setData(record.filterData ?? createEmptyGroup());
      setMainLogic(record.mainLogic || "and");

      const loadedOptionsByField = {};
      if (!skipDropdownLoad) {
        // Load dropdown data sequentially using cascade context from saved values.
        const cascadeContext = {};
        const isWindowClosed = () => fetchGenerationRef.current !== generation;
        for (const field of orderedFields) {
          // Window was closed mid-cascade — stop loading further fields.
          if (fetchGenerationRef.current !== generation) {
            setFetchLoadingFields(new Set());
            setAdvanceLoadingFields(new Set());
            return;
          }
          const action = actions[field];
          if (action && typeof action === "function") {
            try {
              const result = await action(cascadeContext, { isInitialLoad: false, isWindowClosed });
              loadedOptionsByField[field] = optionsFromActionResult(result);
              const savedVal = savedValues[field];
              if (savedVal != null && savedVal !== "") {
                cascadeContext[field] = savedVal;
              }
            } catch (err) {
              console.error(`Failed to load data for field ${field}:`, err);
            } finally {
              setFetchLoadingFields((prev) => {
                const next = new Set(prev);
                next.delete(field);
                return next;
              });
            }
          }
        }
      }

      // Hydrate display text and restore Select All option objects.
      // Skip fieldConfig.data when skipDropdownLoad — those options belong to the
      // page filter bar / last cascade (e.g. FY-2023), not this saved query
      // (FY-2026 + full Specialty/Group/Provider lists). Remapping against them
      // collapsed IN values to the first match that happened to exist.
      const flattenOptionId = (v) => (v != null && typeof v === "object" ? (v.id ?? v) : v);
      const optionText = (o) => (o != null && typeof o === "object" ? o.text : String(o ?? ""));
      const hydrateFilterData = (data) => {
        if (!data || !data.items || !Array.isArray(data.items)) {
          return data;
        }

        return {
          ...data,
          items: data.items.map((item) => {
            if (item.items && Array.isArray(item.items)) {
              return hydrateFilterData(item);
            }

            const field = getItemField(item);
            if (!field || !item.inputValue) return item;

            const fieldConfig = (fieldConfigMapRef.current ?? fieldConfigMap)[field];
            const options = loadedOptionsByField[field]?.length
              ? loadedOptionsByField[field]
              : skipDropdownLoad
                ? []
                : fieldConfig?.data;
            if (!options || !Array.isArray(options) || !options.length) return item;

            const rawValue = item.inputValue.value;
            const selectAllOption = options.find((opt) => opt.id === "Select All");
            const isInOp = item.operator?.operator_value === "IN" || item.operator?.operator_value === "NOT IN";
            const isSelectAllVal = (v) => v === 0 || v === "0" || v === "Select All";

            if (isInOp) {
              const arr = Array.isArray(rawValue) ? rawValue : rawValue != null ? [rawValue] : [];
              if (!arr.length) return item;
              const mapped = arr.map((v) => {
                const flat = flattenOptionId(v);
                if (selectAllOption && isSelectAllVal(flat)) return selectAllOption;
                return options.find((opt) => String(opt.id) === String(flat)) ?? v;
              });
              return {
                ...item,
                inputValue: {
                  ...item.inputValue,
                  value: Array.isArray(rawValue) ? mapped : mapped[0],
                  text: mapped.map(optionText).join(", "),
                },
              };
            }

            if (rawValue == null) return item;
            const valueToMatch = flattenOptionId(Array.isArray(rawValue) ? rawValue[0] : rawValue);
            if (valueToMatch == null) return item;
            if (selectAllOption && isSelectAllVal(valueToMatch)) {
              const restoredValue = Array.isArray(rawValue) ? [selectAllOption] : selectAllOption;
              return {
                ...item,
                inputValue: { ...item.inputValue, value: restoredValue, text: selectAllOption.text },
              };
            }
            const matchingOption = options.find((opt) => String(opt.id) === String(valueToMatch));
            if (matchingOption) {
              return {
                ...item,
                inputValue: {
                  ...item.inputValue,
                  value: Array.isArray(rawValue) ? [matchingOption] : matchingOption,
                  text: matchingOption.text,
                },
              };
            }

            return item;
          }),
        };
      };

      const hydratedFilterData = hydrateFilterData(record.filterData ?? createEmptyGroup());

      // Final check — don't overwrite state that Effect 1 already restored on re-open.
      if (fetchGenerationRef.current !== generation) return;

      setData(hydratedFilterData);
      if (!skipDropdownLoad) {
        setAdvanceLoadingFields((prev) => {
          const next = new Set(prev);
          for (const f of orderedFields) next.delete(f);
          return next;
        });
      }

      const loadedCount = normalizeFilterGroups(collectFilters(hydratedFilterData, { isAdvanceFilter: true })).length;
      setExpressionCounter(loadedCount);

      return {
        hydratedFilterData,
        record,
        mainLogic: record.mainLogic || "and",
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dbObjName, filterColumns, module, operatorList, apiService, fieldConfigMap, actions, cascadePreferredOrder, clearDropdownData]
  );

  // Per-field "cascade already fetched" bookkeeping — cleared whenever the
  // query is reset so dropdowns refetch instead of reusing pinned lists.
  const calledOnceRef = React.useRef({});

  const onCreateNewQuery = React.useCallback(() => {
    const baseForm = resetFormFields();
    setFormFields(baseForm);
    setErrObj(resetErrors());
    setGridHistoryId("");
    setAppliedFilterQuery("");
    setIsFieldChange(false);
    setMainLogic("and");
    setExpressionCounter(0);
    setGroupCounter(0);
    setToggleValues(toggleFieldDefs.reduce((acc, { field }) => ({ ...acc, [field]: false }), {}));
    // A new query must not inherit the previous query's cascaded dropdown
    // lists (a saved "one provider" query pins the Provider list to that one
    // entry, and it survived into Create new query). Clear the fetched lists
    // and the fetched-once guards — same cleanup as the external resetSignal
    // path — so each new expression refetches for the new query's own context.
    calledOnceRef.current = {};
    clearDropdownData();
    baselineRef.current = {
      formFieldsJson: JSON.stringify(baseForm),
      gridHistoryId: "",
    };
    if (showDefaultExpression && defaultExpressionField && columns.length && Object.keys(operatorList || {}).length) {
      const fieldColumn = columns.find((c) => c.field === defaultExpressionField);
      if (fieldColumn) {
        const defaultGroup = getDefaultExpressionData(
          columns,
          operatorList,
          fieldColumn.field,
          getOperatorListValue(fieldColumn.type, operatorList),
          fieldConfigMapRef.current
        );
        setData(defaultGroup || createEmptyGroup());
        setExpressionCounter(defaultGroup?.items?.length || 0);
        return;
      }
    }
    setData(createEmptyGroup());
  }, [toggleFieldDefs, showDefaultExpression, defaultExpressionField, columns, operatorList, clearDropdownData]);


  React.useEffect(() => {
    if (resetSignal === 0) return;
    onCreateNewQuery();
    calledOnceRef.current = {};
    setToggleValues({});
    // Clear all dropdown local state + redux via the hook's clearFieldData map.
    const clearFieldData = hookData?.clearFieldData;
    if (clearFieldData) {
      Object.values(clearFieldData).forEach((fn) => {
        if (typeof fn === "function") fn();
      });
    }
  }, [resetSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stable refs used by the cascade-value validator effect below.
  const addDataRef = React.useRef(addData);
  addDataRef.current = addData;
  const actionsRef = React.useRef(actions);
  actionsRef.current = actions;
  React.useEffect(() => {
    if (fetchLoadingFields.size > 0 || advanceLoadingFields.size > 0) return;
    const currentData = addDataRef.current;
    const currentActions = actionsRef.current;
    if (!currentData?.items?.length || !currentActions) return;

    let hasChange = false;
    const updatedItems = currentData.items.map((item) => {
      if (item.items) return item; // skip group nodes
      const field = item.value?.field;
      if (!currentActions[field]) return item;

      const config = effectiveFieldConfigMap?.[field];
      const data = config?.data;
      if (!data?.length) return item; // data not yet loaded / still masked

      const currentVal = item.inputValue?.value;
      const isEmpty =
        currentVal === null ||
        currentVal === undefined ||
        currentVal === "" ||
        (Array.isArray(currentVal) && currentVal.length === 0);

      if (Array.isArray(currentVal)) {
        // Select All is a valid "all" state — never replace it.
        if (isSelectAllSelection(currentVal)) return item;

        // Keep only items whose id still exists in the refreshed list.
        const stillValid = currentVal.filter(
          (v) => !isSelectAllId(flatId(v)) && data.some((d) => String(d.id) === String(flatId(v)))
        );
        if (stillValid.length !== currentVal.length) {
          hasChange = true;
          const nextVal = stillValid.length > 0 ? stillValid : data[0] ? [data[0]] : [];
          return {
            ...item,
            inputValue: { ...item.inputValue, value: nextVal },
            errorInfo:
              Array.isArray(nextVal) && nextVal.length === 0
                ? { error: true, errorMsg: "Value is required" }
                : { error: false, errorMsg: "" },
          };
        }
      } else if (isEmpty) {
        hasChange = true;
        return {
          ...item,
          inputValue: { ...item.inputValue, value: data[0] },
          errorInfo: { error: false, errorMsg: "" },
        };
      } else {
        // Single-select: replace with first item if current value no longer exists.
        if (!data.some((d) => String(d.id) === String(flatId(currentVal)))) {
          hasChange = true;
          return {
            ...item,
            inputValue: { ...item.inputValue, value: data[0] },
            errorInfo: { error: false, errorMsg: "" },
          };
        }
      }
      return item;
    });

    if (hasChange) {
      setData((prev) => ({ ...prev, items: updatedItems }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveFieldConfigMap, fetchLoadingFields, advanceLoadingFields]);

  const markCascadeCalled = React.useCallback((field) => {
    calledOnceRef.current[field] = true;
  }, []);

  // On expression remove: reset lazy-load guard and clear any active mask.
  const handleRemoveExpression = React.useCallback((field) => {
    if (!field) return;
    delete calledOnceRef.current[field];
    setAdvanceLoadingFields((prev) => {
      if (!prev.has(field)) return prev;
      const next = new Set(prev);
      next.delete(field);
      return next;
    });
  }, []);

  // On field fetch end: unmask the field so freshly loaded data becomes visible.
  const handleFieldFetchEnd = React.useCallback((field) => {
    if (!field) return;
    setAdvanceLoadingFields((prev) => {
      if (!prev.has(field)) return prev;
      const next = new Set(prev);
      next.delete(field);
      return next;
    });
  }, []);

  const handleFieldSpecificActions = React.useCallback(
    (field, dropDownControl) => {
      if (!dropDownControl) {
        // Mask cascade fields while handleAddExpression fetches them.
        if (cascadeFields.includes(field)) {
          setAdvanceLoadingFields((prev) => new Set([...prev, field]));
        }
        return;
      }

      // User opened dropdown manually — fetch if not already called.
      if (calledOnceRef.current[field]) return;
      if (!actions[field]) return;

      calledOnceRef.current[field] = true;
      setAdvanceLoadingFields((prev) => new Set([...prev, field]));

      const cascadeContext = {};
      const items = addData?.items ?? [];
      for (const it of items) {
        if (!it || it.items) continue;
        const f = it.value?.field;
        const v = it.inputValue?.value;
        if (f && f !== field && v !== null && v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0)) {
          cascadeContext[f] = v;
        }
      }

      Promise.resolve(actions[field](cascadeContext, { isInitialLoad: false })).finally(() => {
        setAdvanceLoadingFields((prev) => {
          const next = new Set(prev);
          next.delete(field);
          return next;
        });
      });
    },
    [actions, addData, cascadeFields]
  );

  const hasAnyEdits = React.useMemo(() => {
    const currentFormJson = JSON.stringify(formFields);
    const baseline = baselineRef.current;
    const formChanged = currentFormJson !== baseline.formFieldsJson;
    const historyChanged = Boolean(gridHistoryId) && gridHistoryId !== baseline.gridHistoryId;
    return Boolean(isFieldChange || formChanged || historyChanged);
  }, [formFields, gridHistoryId, isFieldChange]);

  const onCreateLinkClick = React.useCallback(
    (e) => {
      e?.preventDefault?.();
      if (hasAnyEdits) {
        setCreateNewQueryClick(true);
        return;
      }
      onCreateNewQuery();
    },
    [hasAnyEdits, onCreateNewQuery]
  );

  const onCloseConfirmPopup = React.useCallback(() => {
    setCreateNewQueryClick(false);
  }, []);

  const createNewLinkConfirm = React.useCallback(() => {
    onCreateNewQuery();
    setCreateNewQueryClick(false);
  }, [onCreateNewQuery]);

  const onFieldChange = React.useCallback((e) => {
    setIsFieldChange(true);
    setFormFields((prev) => ({
      ...prev,
      [e.target.name]: e.value ?? e.target.value,
    }));
  }, []);

  const validateRequiredFields = React.useCallback(() => {
    if (!requiredFields?.length) return true;
    const presentFields = new Set(
      (addData?.items || []).filter((it) => !it.items && it.value?.field).map((it) => it.value.field)
    );
    const missing = requiredFields.filter(({ field }) => !presentFields.has(field)).map(({ label }) => label);
    if (missing.length) {
      showErrorMsg(`Please add a filter expression for: ${missing.join(" and ")}`);
      return false;
    }
    return true;
  }, [addData, requiredFields, showErrorMsg]);

  const onSearchClick = React.useCallback(async () => {
    if (!validateRequiredFields()) return;
    // "Value is required" under the field instead of silently applying.
    if (!handleFieldValidation()) return;
    const { isValid } = checkNumberOfExpressions();
    console.log("Expression count validation:", isValid, {
      expressionCounter,
      filterColumnsLength: filterColumns?.length,
    });
    if (!isValid) {
      return showErrorMsg(errorMessage);
    }

    const tempData = Array.isArray(addData) ? [...addData] : { ...addData };
    const filterObj = collectFilters(tempData, { isAdvanceFilter: true });
    const appliedFilterTooltip = toolTipMsg(filterObj);

    if (onApply) {
      onApply({
        filterObj,
        filterColumns,
        operatorList,
        mainLogic: mainLogic || addData?.logic,
        filterData: addData,
        formFields: { ...formFields },
        gridHistoryId,
        appliedFilterTooltip,
        appliedFilterQuery,
        toggleValues,
      });
    }

    handleClose();
  }, [
    addData,
    appliedFilterQuery,
    checkNumberOfExpressions,
    filterColumns,
    formFields,
    gridHistoryId,
    mainLogic,
    onApply,
    onClose,
    toggleValues,
    toolTipMsg,
  ]);

  const handleDateValidation = () => {
    let isValid = true;
    let newErrObj = {};
    setErrObj(newErrObj);
    return { isValid: isValid, newErrObj: newErrObj };
  };

  const handleValidation = async () => {
    let isValid = true;
    let newErrObj = { filter_name: "" };
    if (formFields.filter_name === undefined || formFields.filter_name.trim() === "") {
      isValid = false;
      newErrObj.filter_name = MessageSchema.filterNameRequired;
    }
    let isDateValid = await handleDateValidation();
    if (!isDateValid.isValid) {
      isValid = false;
    }
    setErrObj({ ...newErrObj, ...isDateValid.newErrObj });
    return isValid;
  };

  const handleFieldValidation = () => {
    let isValid = true;

    // A dropdown expression is empty when nothing real is selected. "Select All"
    // over a finished empty list is still empty — the UI shows no value.
    const isEmptyDropdownSelection = (item) => {
      if (!item?.value?.dropDownControl) return false;
      const opVal = item?.operator?.operator_value;
      if (opVal === "IS NULL" || opVal === "IS NOT NULL") return false;
      const raw = item.inputValue?.value;
      const arr = Array.isArray(raw) ? raw : raw == null || raw === "" ? [] : [raw];
      if (arr.length === 0) return true;
      if (arr.some((v) => !isSelectAllId(flatId(v)))) return false;
      const options = fieldConfigMap?.[item.value.field]?.data || [];
      return !options.some((o) => !isSelectAllId(flatId(o)));
    };

    const validateItems = (items) => {
      return items.map((item) => {
        const rawValue = item.inputValue?.value ?? "";
        const validationKey = item.value?.type;

        const validationResult = isEmptyDropdownSelection(item)
          ? { error: true, errorMsg: "Value is required" }
          : handleRenderValidation(Array.isArray(rawValue) && rawValue.length === 0 ? "" : rawValue, {
              key: validationKey,
              errorMsg: item?.value?.required_msg,
              operator_value: item?.operator?.operator_value,
              field: item?.value?.field,
              dropDownControl: item?.value?.dropDownControl,
            });

        if (validationResult.error) {
          isValid = false;
        }
        const updatedChildren = item.items?.length ? validateItems(item.items) : item.items;

        return {
          ...item,
          errorInfo: validationResult,
          items: updatedChildren,
        };
      });
    };
    if (addData?.items?.length) {
      const updatedItems = validateItems(addData.items);
      console.log("Updated Items with Validation Info:", updatedItems, addData);
      if (!isValid) {
        setData({ ...addData, items: updatedItems });
      }
    }

    return isValid;
  };

  const onSaveFilterClick = React.useCallback(async () => {
    if (!validateRequiredFields()) return;
    if (!handleFieldValidation()) return;

    const isFormValid = await handleValidation();
    if (!isFormValid) return;

    const tempData = structuredClone(addData);
    const filterObj = collectFilters(tempData, { isAdvanceFilter: true });
    const newFilterObj = structuredClone(filterObj);
    const reqData = {
      in_filter_name: formFields.filter_name.trim(),
      in_db_obj_code: dbObjName,
      in_filter_desc: formFields?.filter_desc?.trim() || "",
      in_is_favorite: formFields.isFavorite ? 1 : 0,
      filterData: newFilterObj,
      module: module,
      filterColumns,
      topFilters: {},
      toggleValues,
    };
    setMode("save");
    const res = await apiService.SaveFilterQuery(reqData);
    if (!res.success) {
      if (res.errorMsg) {
        showErrorMsg(res.errorMsg);
      }
      return;
    }
    if (res.success && formFields.applyToGrid) {
      const newHistoryId = res.out_last_insert_key;
      setGridHistoryId(newHistoryId);
      const appliedFilterTooltip = toolTipMsg(filterObj);

      // skipDropdownLoad=true — window closes immediately after, no need to fire cascade APIs.
      const loaded = await getDataById(newHistoryId, { skipDropdownLoad: true });
      const realFilterText = loaded?.record?.filter_text || "";
      const realFormFields = loaded
        ? {
            filter_name: loaded.record.filter_name || formFields.filter_name,
            filter_desc: loaded.record.filter_desc || formFields.filter_desc,
            isFavorite: Boolean(Number(loaded.record.is_favorite)),
            version: loaded.record.version || 0,
            applyToGrid: false,
          }
        : { ...formFields, version: 0, applyToGrid: false };

      if (onSavedFilterUpdate) {
        onSavedFilterUpdate({
          formFields: realFormFields,
          filterData: loaded?.hydratedFilterData || addData,
          mainLogic: loaded?.mainLogic || mainLogic || addData?.logic,
          gridHistoryId: newHistoryId,
          appliedFilterQuery: realFilterText,
          toggleValues,
        });
      }
      if (onApply) {
        onApply({
          filterObj,
          filterColumns,
          operatorList,
          mainLogic: mainLogic || addData?.logic,
          filterData: addData,
          formFields: realFormFields,
          gridHistoryId: newHistoryId,
          appliedFilterTooltip,
          appliedFilterQuery: realFilterText,
          toggleValues,
        });
      }
      if (onClose) onClose({ hasExpressions: expressionCounterRef.current > 0 });
    } else {
      setGridHistoryId(res.out_last_insert_key);
      const loaded = await getDataById(res.out_last_insert_key);
      if (loaded && onSavedFilterUpdate) {
        const { hydratedFilterData, record, mainLogic: loadedLogic } = loaded;
        const savedFormFields = {
          filter_name: record.filter_name || "",
          filter_desc: record.filter_desc || "",
          isFavorite: Boolean(Number(record.is_favorite)),
          version: record.version || 0,
          applyToGrid: false,
        };
        onSavedFilterUpdate({
          formFields: savedFormFields,
          filterData: hydratedFilterData,
          mainLogic: loadedLogic,
          gridHistoryId: record.grid_filter_history_key,
          appliedFilterQuery: record.filter_text || "",
          toggleValues,
        });
      }
      dispatch(
        setNewMessage({
          message: "Query saved successfully!",
          type: "Success",
          show: true,
        })
      );
    }
  }, [
    addData,
    appliedFilterQuery,
    dbObjName,
    dispatch,
    filterColumns,
    formFields,
    getDataById,
    mainLogic,
    module,
    onApply,
    onClose,
    onSavedFilterUpdate,
    operatorList,
    toggleValues,
    toolTipMsg,
    apiService,
    validateRequiredFields,
    showErrorMsg,
  ]);

  // eslint-disable-next-line no-unused-vars
  const onSaveFilter = onSaveFilterClick;

  const onSelectSavedQuery = React.useCallback(
    async (item) => {
      setShowSavedList(false);
      calledOnceRef.current = {};
      clearDropdownData();
      const dropdownFields = Object.keys(actions || {});
      setAdvanceLoadingFields(new Set(dropdownFields));
      setFetchLoadingFields(new Set(dropdownFields));
      setData(createEmptyGroup());
      await getDataById(item.grid_filter_history_key);
    },
    [getDataById, clearDropdownData, actions]
  );

  // Direct apply from saved list — close window first, load in background.
  const onApplySavedQuery = React.useCallback(
    async (item) => {
      setShowSavedList(false);
      onClose?.({ hasExpressions: expressionCounterRef.current > 0 });
      if (onDirectApplyLoading) onDirectApplyLoading(true);
      let loaded;
      try {
        // Load dropdown lists for the saved query's own cascade context (NOT
        // skipDropdownLoad): the funnel tooltip resolves ids to names through
        // these lists, and skipping left it showing raw keys ("Group IN 158",
        // provider ids). Costs the same cascade calls a popup Search fires.
        loaded = await getDataById(item.grid_filter_history_key);
      } finally {
        if (onDirectApplyLoading) onDirectApplyLoading(false);
      }
      if (!loaded) return;

      const { hydratedFilterData, record, mainLogic: loadedLogic } = loaded;
      const filterObj = collectFilters(hydratedFilterData, { isAdvanceFilter: true });
      const appliedFilterTooltip = toolTipMsg(filterObj);
      const loadedFormFields = {
        filter_name: record.filter_name || "",
        filter_desc: record.filter_desc || "",
        isFavorite: Boolean(Number(record.is_favorite)),
        version: record.version || 0,
      };
      // Use the toggle values stored in the saved record, not the current component state.
      const loadedToggleValues = record.toggleValues || {};

      if (onApply) {
        onApply({
          filterObj,
          filterColumns,
          operatorList,
          mainLogic: loadedLogic,
          filterData: hydratedFilterData,
          formFields: loadedFormFields,
          gridHistoryId: record.grid_filter_history_key,
          appliedFilterTooltip,
          appliedFilterQuery: record.filter_text || "",
          toggleValues: loadedToggleValues,
        });
      }
    },
    [getDataById, filterColumns, operatorList, onApply, onClose, onDirectApplyLoading, toolTipMsg]
  );

  const onUpdateClick = React.useCallback(async () => {
    if (!validateRequiredFields()) return;
    if (!handleFieldValidation()) return;
    const isFormValid = await handleValidation();
    if (!isFormValid) return;

    const tempData = structuredClone(addData);
    const filterObj = collectFilters(tempData, { isAdvanceFilter: true });
    const newFilterObj = structuredClone(filterObj);
    const reqData = {
      in_filter_name: formFields.filter_name.trim(),
      in_db_obj_code: dbObjName,
      in_filter_desc: formFields?.filter_desc?.trim() || "",
      in_is_favorite: formFields.isFavorite ? 1 : 0,
      in_grid_filter_history_key: gridHistoryId,
      in_version: formFields.version,
      filterData: newFilterObj,
      module: module,
      filterColumns: filterColumns,
      topFilters: {},
      toggleValues,
    };
    setMode("update");
    const res = await apiService.UpdateFilterQuery(reqData);
    if (!res?.success) {
      if (res?.errorMsg) {
        showErrorMsg(res.errorMsg);
      }
      return;
    }
    // skipDropdownLoad=true — dialog stays open with dropdowns already populated; only need the updated version from DB.
    await getDataById(gridHistoryId, { skipDropdownLoad: true });
    const updatedFormFields = { ...formFields, version: (formFields.version || 0) + 1 };
    if (onSavedFilterUpdate) {
      onSavedFilterUpdate({
        formFields: updatedFormFields,
        filterData: addData,
        mainLogic,
        gridHistoryId,
        appliedFilterQuery,
        toggleValues,
      });
    }
    dispatch(
      setNewMessage({
        message: "Query updated successfully!",
        type: "Success",
        show: true,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    addData,
    appliedFilterQuery,
    dbObjName,
    filterColumns,
    formFields,
    getDataById,
    gridHistoryId,
    mainLogic,
    module,
    onSavedFilterUpdate,
    apiService,
    showErrorMsg,
    toggleValues,
  ]);

  // Updates the saved filter and immediately runs the search.
  const onSearchAndUpdateClick = React.useCallback(async () => {
    if (!validateRequiredFields()) return;
    if (!handleFieldValidation()) return;
    const isFormValid = await handleValidation();
    if (!isFormValid) return;

    const { isValid } = checkNumberOfExpressions();
    if (!isValid) {
      return showErrorMsg(errorMessage);
    }

    const tempData = structuredClone(addData);
    const filterObj = collectFilters(tempData, { isAdvanceFilter: true });
    const newFilterObj = structuredClone(filterObj);
    const reqData = {
      in_filter_name: formFields.filter_name.trim(),
      in_db_obj_code: dbObjName,
      in_filter_desc: formFields?.filter_desc?.trim() || "",
      in_is_favorite: formFields.isFavorite ? 1 : 0,
      in_grid_filter_history_key: gridHistoryId,
      in_version: formFields.version,
      filterData: newFilterObj,
      module: module,
      filterColumns: filterColumns,
      topFilters: {},
      toggleValues,
    };
    setMode("search-update");
    const res = await apiService.UpdateFilterQuery(reqData);
    if (!res?.success) {
      if (res?.errorMsg) {
        showErrorMsg(res.errorMsg);
      }
      return;
    }
    const appliedFilterTooltip = toolTipMsg(filterObj);
    const updatedFormFields = { ...formFields, version: (formFields.version || 0) + 1 };
    setFormFields(updatedFormFields);
    if (onApply) {
      onApply({
        filterObj,
        filterColumns,
        operatorList,
        mainLogic: mainLogic || addData?.logic,
        filterData: addData,
        formFields: updatedFormFields,
        gridHistoryId,
        appliedFilterTooltip,
        appliedFilterQuery,
        toggleValues,
      });
    }
    handleClose();
  }, [
    addData,
    appliedFilterQuery,
    checkNumberOfExpressions,
    dbObjName,
    filterColumns,
    formFields,
    gridHistoryId,
    mainLogic,
    module,
    onApply,
    onClose,
    operatorList,
    showErrorMsg,
    toggleValues,
    toolTipMsg,
    apiService,
  ]);

  if (!open) return null;

  return (
    <>
      <Window
        title={title}
        onClose={handleClose}
        width={window.innerWidth * 0.9}
        height={window.innerHeight * 0.9}
        resizable={false}
      >
        {loadingState && <LoadingPanel />}

        <div className="saved-query-list-container">
          <Label className="qbuilderoptiontitle">
            {"Filter Options"}{" "}
            <div className="saved-query-list">
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a
                href="#"
                onClick={onCreateLinkClick}
                style={isActionDisabled ? { pointerEvents: "none", opacity: 0.4, cursor: "not-allowed" } : undefined}
                title={actionDisabledTitle}
              >
                Create new query{" "}
              </a>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (!isActionDisabled) setShowSavedList(true);
                }}
                style={isActionDisabled ? { pointerEvents: "none", opacity: 0.4, cursor: "not-allowed" } : undefined}
                title={actionDisabledTitle}
              >
                View saved query list
              </a>
            </div>
          </Label>

          <div className="queryfiltercontainer sticky-header-query-builder">
            <div className="queryfiltername">
              <label htmlFor="filter_name">Filter Query Name *</label>
              <Input
                id="filter_name"
                name="filter_name"
                value={formFields.filter_name}
                onChange={onFieldChange}
                placeholder="Enter filter query name"
              />
              {errObj.filter_name ? <div className="k-text-error">{errObj.filter_name}</div> : null}
            </div>

            <div className="queryfiltername">
              <label htmlFor="filter_desc" style={{ display: "block" }}>
                Filter Query Description
              </label>
              <TextArea
                id="filter_desc"
                name="filter_desc"
                value={formFields.filter_desc}
                onChange={onFieldChange}
                placeholder="Enter filter query description"
                rows={2}
              />
            </div>

            <div className="favoriteswitch">
              <label htmlFor="is_favorite">Is Favorite ?</label>
              <Switch
                id="is_favorite"
                checked={formFields.isFavorite}
                onChange={(e) =>
                  onFieldChange({
                    target: {
                      name: "isFavorite",
                      value: e.value,
                    },
                  })
                }
              />
            </div>

            {gridHistoryId === "" && (
              <div className="savefilterquery">
                <label htmlFor="apply_to_grid">Save filter query and Apply to Grid</label>
                <Switch
                  id="apply_to_grid"
                  checked={formFields.applyToGrid}
                  onChange={(e) =>
                    onFieldChange({
                      target: {
                        name: "applyToGrid",
                        value: e.value,
                      },
                    })
                  }
                />
              </div>
            )}
          </div>
        </div>

        {typeof renderTopFilters === "function" ? renderTopFilters() : null}

        {/* Base Filter section omitted for report modules */}
        <div
          className="queryfiltercontainer"
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "10px",
            marginTop: "5px",
            gap: "10px",
          }}
        >
          <Label className="qbuilderoptiontitle">{" Additional Filter"}</Label>
        </div>

        {Object.keys(operatorList).length === 0 || filterColumns.length === 0 ? (
          <LoadingPanel />
        ) : (
          <RenderGroup
            group={addData}
            onChange={setData}
            isRoot={true}
            module={module}
            filterColumns={filterColumns}
            originalFilterColumns={originalFilterColumns}
            operatorList={operatorList}
            fieldConfigMap={effectiveFieldConfigMap}
            onFieldSpecificAction={handleFieldSpecificActions}
            markCascadeCalled={markCascadeCalled}
            onRemoveExpression={handleRemoveExpression}
            onFieldFetchEnd={handleFieldFetchEnd}
            externalCascadeLoadingSet={fetchLoadingFields}
            lockedFieldsRef={calledOnceRef}
            appliedFilterQuery={appliedFilterQuery}
            gridHistoryId={gridHistoryId}
            setFormFields={setFormFields}
            formFields={formFields}
            errObj={errObj}
            setGroupCounter={setGroupCounter}
            groupCounter={groupCounter}
            setExpressionCounter={setExpressionCounter}
            expressionCounter={expressionCounter}
            hasParsedRef={{ current: false }}
            actions={actions}
            isResetTopFilters={false}
            setIsChange={() => {}}
            setErrObj={setErrObj}
            mainLogic={mainLogic}
            setMainLogic={setMainLogic}
            isFieldChange={isFieldChange}
            setIsFieldChange={setIsFieldChange}
            isChangeFilter={false}
            setIsChangeFilter={() => {}}
            toggleFieldDefs={toggleFieldDefs}
            toggleValues={toggleValues}
            onToggleChange={(field, val) => {
              setToggleValues((prev) => ({ ...prev, [field]: val }));
              setIsFieldChange(true);
            }}
            requiredFields={requiredFields}
            onCascadeActiveChange={setIsCascadeActive}
            lockedExpressionField={defaultExpressionField}
          />
        )}

        <WindowActionsBar layout={"end"}>
          <div className="query-builder-footer">
            <div className="filter-info-msg">
              <i className="fas fa-info-circle"></i>
              {errorMessage}
            </div>
            <div className="filter-button-container">
              <Button
                primary={true}
                className="k-button cancelbtn"
                onClick={onSearchClick}
                title={actionDisabledTitle}
                disabled={
                  isActionDisabled || expressionCounter === 0 || (gridHistoryId !== "" ? false : !isFieldChange)
                }
              >
                Search
              </Button>

              {appliedFilterQuery && gridHistoryId !== "" && (
                <Button
                  className="primarybtn k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
                  onClick={onUpdateClick}
                  title={actionDisabledTitle}
                  disabled={isActionDisabled || !isFieldChange || expressionCounter === 0}
                >
                  Update
                </Button>
              )}

              {/* Save (new) or Search & Update (existing) */}
              {appliedFilterQuery && gridHistoryId !== "" ? (
                <Button
                  className="primarybtn k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
                  onClick={onSearchAndUpdateClick}
                  title={actionDisabledTitle}
                  disabled={isActionDisabled || !isFieldChange || expressionCounter === 0}
                >
                  Search &amp; Update
                </Button>
              ) : (
                <Button
                  className="primarybtn k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
                  onClick={onSaveFilterClick}
                  title={actionDisabledTitle}
                  disabled={isActionDisabled || !isFieldChange || expressionCounter === 0}
                >
                  Save
                </Button>
              )}
            </div>
          </div>
        </WindowActionsBar>
      </Window>

      {isCreateNewQueryClick && (
        <Window title={"Confirmation"} onClose={onCloseConfirmPopup} width={400} height={200} resizable={false}>
          Creating a new query will reset your current filters and inputs. Do you want to continue?
          <WindowActionsBar layout={"end"}>
            <Button className="k-button cancelbtn" onClick={onCloseConfirmPopup}>
              Cancel
            </Button>

            <Button className="primarybtn k-button" onClick={createNewLinkConfirm}>
              Yes
            </Button>
          </WindowActionsBar>
        </Window>
      )}

      {showSavedList && (
        <ReportSavedQueryGrid
          visible={showSavedList}
          onClose={() => setShowSavedList(false)}
          onSelect={onSelectSavedQuery}
          onSelectAndApply={onApplySavedQuery}
          dbObjName={dbObjName}
          module={module}
        />
      )}

      {tooltipString ? (
        <div style={{ display: "none" }} aria-hidden>
          {tooltipString}
        </div>
      ) : null}
    </>
  );
};

export default AdvanceQueryFilter;
